import { useEffect, useState } from "react";
import "./style.css";
import useSubjectsFetch from "../../hooks/useFetchSubjects";
import { BubbleLoader } from "../../components/loader";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { playSpeech, playSpeechTTS } from "../../utils/playSpeech";
import { playSpeechKokoro } from "../../utils/playSpeechKokoro";
import { stopSpeechKokoro } from "../../utils/playSpeechKokoro";

function InlineSVG({ url, subjectName }) {
  const [svgContent, setSvgContent] = useState(null);

  useEffect(() => {
    fetch(url)
      .then((res) => res.text())
      .then(setSvgContent)
      .catch((err) => console.error("Error loading SVG:", err));
  }, [url]);

  useEffect(() => {
    if (!svgContent) return;

    const container = document.getElementById(
      `inline-sub-svg-container-${subjectName}`,
    );
    if (!container) return;

    const allImages = container.querySelectorAll("image");
    if (!allImages.length) return;

    allImages.forEach((img) => {
      img.style.transition = "transform 0.5s ease-in-out";
      img.style.transformOrigin = "center";
      img.style.transformBox = "fill-box";
    });

    container.addEventListener("mouseenter", () => {
      allImages.forEach((img) => {
        img.setAttribute("transform", "translate(2,-5) scale(1.05)");
      });
    });

    container.addEventListener("mouseleave", () => {
      allImages.forEach((img) => {
        img.setAttribute("transform", "translate(0,0) scale(1)");
      });
    });
  }, [svgContent, subjectName]);

  if (!svgContent) return null;

  return (
    <div
      id={`inline-sub-svg-container-${subjectName}`}
      className="sub-object"
      dangerouslySetInnerHTML={{ __html: svgContent }}
    />
  );
}

function SubjectsPage({ handleClick }) {
  const [subjects, loading] = useSubjectsFetch();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(3);
  const [showLoader, setShowLoader] = useState(true);
  const [ttsError, setTtsError] = useState(null);

  const handleSubjectHover = (subjectName) => {
    console.log("Hovering over:", subjectName);
    playSpeechKokoro(subjectName);
    console.log("TTS function executed for:", subjectName);
  };
  useEffect(() => {
    const timer = setTimeout(() => setShowLoader(false), 1000);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      if (width >= 1321) {
        setItemsPerPage(3);
      } else if (width >= 1001) {
        setItemsPerPage(2);
      } else if (width >= 768) {
        setItemsPerPage(1);
      } else {
        setItemsPerPage(3);
      }
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Reset currentIndex when itemsPerPage changes
  useEffect(() => {
    setCurrentIndex(0);
  }, [itemsPerPage]);

  const totalPages = Math.ceil(subjects.length / itemsPerPage);

  const handleNext = () => {
    if (currentIndex < totalPages - 1) setCurrentIndex(currentIndex + 1);
  };

  const handlePrev = () => {
    if (currentIndex > 0) setCurrentIndex(currentIndex - 1);
  };

  const start = currentIndex * itemsPerPage;
  const visibleSubjects = subjects.slice(start, start + itemsPerPage);

  return (
    <div className="content-box">
      <div className="welc-text">
        <h2 className="welcome-text">Welcome to Learning Lab</h2>
        <h4 className="wel-sub-text">Explore to Genius!</h4>
      </div>

      {loading || showLoader ? (
        <BubbleLoader />
      ) : (
        <div className="sub-card-container">
          {/* Left Button */}
          {subjects.length > itemsPerPage && (
            <button
              onClick={handlePrev}
              disabled={currentIndex === 0}
              className="nav-btn side-btn sub-prev-btn"
            >
              <ChevronLeft size={30} />
            </button>
          )}

          {/* Cards */}
          <div className="sub-card-section">
            {visibleSubjects.map((subject, index) => {
              const isSVG = subject.image.endsWith(".svg");
              return (
                <div
                  className="sub-card"
                  onClick={() =>
                    handleClick(subject.id, subject.name, `/levels`)
                  }
                  onMouseEnter={() => handleSubjectHover(subject.name)}
                  key={index}
                >
                  <div className="sub-svg-container">
                    {isSVG ? (
                      <InlineSVG
                        url={`${subject.image}?t=${Date.now()}`}
                        subjectName={subject.name}
                      />
                    ) : (
                      <img
                        src={subject.image}
                        alt={subject.name}
                        className="sub-object fallback-background"
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                        }}
                      />
                    )}
                  </div>
                  <div className="svg-overlay-text">{subject.name}</div>
                </div>
              );
            })}
          </div>

          {/* Right Button */}
          {subjects.length > itemsPerPage && (
            <button
              onClick={handleNext}
              disabled={currentIndex === totalPages - 1}
              className="nav-btn side-btn sub-next-btn"
            >
              <ChevronRight size={30} />
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export default SubjectsPage;
